import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { NumeralPipe } from '@pipes/numeral.pipe';

import { Role } from '../device-setting-list.component';
import { AssettypeViewModel } from '../device-setting-edit-dialog/device-setting-edit-dialog.component';
import {
  ADMIN_TABLE_COLUMN_LIST,
  GENERAL_TABLE_COLUMN_LIST,
  SYSTEM_ADMIN_TABLE_COLUMN_LIST,
} from './device-setting-list-table';
import { FilterService } from '@services/filter.service';
import { BatteryIconComponent } from '@components/shared/battery-icon/battery-icon.component';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';
import { format } from 'date-fns';
import { DeviceSettingBase } from '@utils/device-setting.util';
import { ColoredTag, tagStyle } from '@utils/tag-color.util';

/** デバイス設定のViewModel */
export type DeviceTelemetryViewModel = {
  /** デバイス名 */
  deviceName: string;
  /** シリアルナンバー */
  serialNo: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** デバイス種類表示名 */
  deviceTypeDisplayName: string;
  /** 最終更新日時 */
  lastCommunicationAt: string;
  /** ステータス */
  status: string;
  /** バッテリー */
  battery: number;
  /** 設定グループ */
  settingGroupName: string;
  /** タグ一覧 */
  tag: string[];
  tagList?: ColoredTag[];
  /** デバイス契約状態 */
  validContractState: boolean;
};

/** デバイスのViewModel */
export type DeviceSettingViewModel = DeviceSettingBase & {
  /** デバイスID */
  serialNo: string;
  /** デバイス名 */
  deviceName?: string;

  /** ステータス */
  status: string;
  /** 最終更新日時 */
  lastUpdatedAt: Date;
  /** 次回更新日時 */
  plannedUpdatedAt: Date;
  /** バッテリー */
  battery: number;
  /** デバイスグループID */
  deviceSettingGroupId?: string;
  /** センサーデータ収集間隔 */
  samplingFrequency: number;
  /** クラウドへのデータ送信間隔 */
  reportingFrequency: number;
  /** フライトモード */
  fmd?: boolean;
};

interface DeviceSettingListFilter {
  deviceName?: string;
  serialNo?: string;
  deviceTypeDisplayName?: string;
  lastCommunicationAt?: string;
  status?: string;
  battery?: string;
  settingGroupName?: string;
  tag?: string;
  sortState?: Sort;
  pageIndex?: number;
}

const pageId = 'DeviceSettingList';

export type DeviceNameUpdate = {
  device: DeviceTelemetryViewModel;
  deviceName: string;
  complete: (saved: boolean) => void;
};

@Component({
  selector: 'app-device-setting-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DatePipe,
    MatMenuModule,
    MatCardModule,
    NumeralPipe,
    BatteryIconComponent,
    DirectivesModule,
  ],
  templateUrl: './device-setting-list-table.component.html',
  styleUrl: './device-setting-list-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceSettingListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  public readonly tagStyle = tagStyle;
  /** デバイス設定詳細 */
  @Input({ required: true }) deviceSetting: unknown;

  /** デバイス設定詳細一覧 */
  @Input({ required: true }) deviceSettingList: DeviceTelemetryViewModel[] = [];

  /** 権限 */
  @Input({ required: true }) role: Role = 'system_admin';

  /** アセットタイプ一覧 */
  @Input() assettypeList: AssettypeViewModel[] = [];

  /** テナント一覧 */
  @Input() tenantList: string[] = [];

  /** デバイス種類の選択肢 */
  @Input() deviceTypeDisplayNames: string[] = [];

  /** 現在のテナント */
  @Input() currentTenant: string = '';

  /** 閲覧のみか */
  @Input() readonly = false;

  /** 新規登録を通知 */
  @Output() created = new EventEmitter<DeviceTelemetryViewModel[]>();

  /** 更新を通知 */
  @Output() updated = new EventEmitter<DeviceTelemetryViewModel>();

  @Output() updatedName = new EventEmitter<DeviceNameUpdate>();

  public editingDeviceId: string | null = null;
  public savingDeviceName = false;
  public deviceNameControl = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.pattern(/\S/),
    ],
  });

  @ViewChild('deviceNameInput') deviceNameInput?: ElementRef<HTMLInputElement>;

  /** タグの更新を通知 */
  @Output() updatedTag = new EventEmitter<DeviceTelemetryViewModel[]>();

  /** 削除を通知 */
  @Output() deleted = new EventEmitter<DeviceTelemetryViewModel[]>();

  /** デバイス詳細設定の更新を通知 */
  @Output() openDeviceEditDialog = new EventEmitter<void>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public displayedColumns: string[] = ADMIN_TABLE_COLUMN_LIST;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<DeviceTelemetryViewModel>([]);

  /** 選択状態 */
  @Input() selection = new SelectionModel<DeviceTelemetryViewModel>(true, []);

  /** デバイス名検索キーワード */
  public deviceNameKeyword = '';

  /** シリアル番号検索キーワード */
  public serialNoKeyword = '';

  /** デバイス種類ID検索キーワード */
  public deviceTypeIdKeyword = '';

  /** 最終通信日時検索キーワード */
  public lastCommunicationAtKeyword = '';

  /** ステータス検索キーワード */
  public statusKeyword = '';

  /** バッテリー検索キーワード */
  public batteryKeyword = '';

  /** 設定グループ検索キーワード */
  public settingGroupNameKeyword = '';

  /** タグ検索キーワード */
  public tagKeyword = '';

  /** テナント名キーワード */
  public tenantNameKeyword = '';

  /**契約期限 */
  public expiredAtKeyword = '';

  /** 選択されたメールステータス */
  public selectedEmailStatus: boolean | undefined;

  /** 選択されたMFA設定 */
  public selectedMfaRequired: boolean | undefined;

  /** 選択されたMFAステータス */
  public selectedMfaStatus: boolean | undefined;

  /** 選択された設定権限 */
  public selectedConfigurable: boolean | undefined;

  /** 選択可能なデバイスタグ一覧 */
  public selectableDeviceTagList: string[] = [];

  /** 選択されたデバイスタグ一覧 */
  public selectedDeviceTagList: string[] = [];

  /** 選択可能なデバイス一覧 */
  public selectableDeviceList: string[] = [];

  /** 選択されたデバイス一覧 */
  public selectedDeviceList: string[] = [];

  /** 選択されたデバイス種類 */
  public selectedDeviceType: string = '';

  /** 初期ソート状態 */
  public initSortState: Sort = {
    active: 'lastCommunicationAt',
    direction: 'desc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  options = [
    {
      name: 'checkbox',
      checked: true,
      disabled: true,
      displayName: 'チェックボックス',
    },
    {
      name: 'deviceName',
      checked: true,
      disabled: true,
      displayName: 'デバイス名',
    },
    {
      name: 'serialNo',
      checked: true,
      disabled: false,
      displayName: 'シリアル番号',
    },
    {
      name: 'deviceTypeId',
      checked: true,
      disabled: false,
      displayName: 'デバイス種類',
    },
    {
      name: 'lastCommunicationAt',
      checked: true,
      disabled: false,
      displayName: '最終更新日時',
    },
    {
      name: 'status',
      checked: true,
      disabled: false,
      displayName: 'ステータス',
    },
    {
      name: 'battery',
      checked: true,
      disabled: false,
      displayName: 'バッテリー',
    },
    {
      name: 'settingGroupName',
      checked: true,
      disabled: false,
      displayName: '設定グループ',
    },
    { name: 'tag', checked: true, disabled: false, displayName: 'タグ' },
  ];

  constructor(
    private filterService: FilterService<
      DeviceSettingListFilter,
      DeviceTelemetryViewModel
    >,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterService.setSessionStorageKey(pageId);
  }

  public canEditDeviceName(device: DeviceTelemetryViewModel): boolean {
    return (
      !this.readonly &&
      (this.role === 'tenant_admin' || this.role === 'tenant_editor') &&
      device.validContractState
    );
  }

  public editDeviceName(device: DeviceTelemetryViewModel): void {
    if (!this.canEditDeviceName(device) || this.savingDeviceName) return;
    this.editingDeviceId = device.serialNo;
    this.deviceNameControl.reset(device.deviceName);
    this.cdr.detectChanges();
    this.deviceNameInput?.nativeElement.focus();
  }

  public cancelDeviceNameEdit(): void {
    if (this.savingDeviceName) return;
    this.editingDeviceId = null;
  }

  public saveDeviceName(device: DeviceTelemetryViewModel): void {
    if (
      this.savingDeviceName ||
      this.deviceNameControl.invalid ||
      !this.canEditDeviceName(device) ||
      this.editingDeviceId !== device.serialNo
    )
      return;
    const deviceName = this.deviceNameControl.value;
    if (deviceName === device.deviceName) {
      this.cancelDeviceNameEdit();
      return;
    }
    this.savingDeviceName = true;
    this.updatedName.emit({
      device,
      deviceName,
      complete: (saved) => {
        this.savingDeviceName = false;
        if (saved) this.editingDeviceId = null;
        this.cdr.markForCheck();
      },
    });
  }

  public ngOnInit(): void {
    this._setDisplayedColumns(this.role);
    this.updateDisplayList();

    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'deviceTypeId') return item.deviceTypeDisplayName;
      const value = item[property as keyof DeviceTelemetryViewModel];
      return typeof value === 'number' ? value : String(value ?? '');
    };

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルター設定
    this.dataSource.filterPredicate = (element: DeviceTelemetryViewModel) => {
      let deviceName = true;
      if (this.deviceNameKeyword) {
        // deviceNameKeywordが空でない場合のみフィルタリングを行う
        deviceName =
          element.deviceName
            .trim()
            .toLowerCase()
            .indexOf(this.deviceNameKeyword.toLowerCase()) > -1;
      }

      let serialNo = true;
      if (this.serialNoKeyword) {
        // serialNoKeywordが空でない場合のみフィルタリングを行う
        serialNo =
          element.serialNo
            .trim()
            .toLowerCase()
            .indexOf(this.serialNoKeyword.toLowerCase()) > -1;
      }

      let deviceTypeDisplayName = true;
      if (this.selectedDeviceType) {
        deviceTypeDisplayName =
          element.deviceTypeDisplayName === this.selectedDeviceType;
      }

      let lastCommunicationAt = true;
      if (this.lastCommunicationAtKeyword) {
        // lastCommunicationAtKeywordが空でない場合のみフィルタリングを行う

        // フィルタリング時に比較できるように画面上の表記形式（yyyy/MM/dd HH:mm:ss）と合わせる
        const searchTarget =
          element.lastCommunicationAt != null
            ? format(
                new Date(element.lastCommunicationAt),
                'yyyy/MM/dd HH:mm:ss',
              )
            : '';
        lastCommunicationAt =
          searchTarget
            .trim()
            .toLowerCase()
            .indexOf(this.lastCommunicationAtKeyword.toLowerCase()) > -1;
      }

      let status = true;
      if (this.statusKeyword) {
        // statusKeywordが空でない場合のみフィルタリングを行う
        status =
          (element.status ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.statusKeyword.toLowerCase()) > -1;
      }

      let battery = true;
      if (this.batteryKeyword) {
        // batteryKeywordが空でない場合のみフィルタリングを行う
        battery =
          (Math.round(element.battery).toString() ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.batteryKeyword.toLowerCase()) > -1;
      }

      let settingGroupName = true;
      if (this.settingGroupNameKeyword) {
        // settingGroupNameKeywordが空でない場合のみフィルタリングを行う
        settingGroupName =
          element.settingGroupName
            .trim()
            .toLowerCase()
            .indexOf(this.settingGroupNameKeyword.toLowerCase()) > -1;
      }

      let tag = true;
      if (this.tagKeyword) {
        // tagKeywordが空でない場合のみフィルタリングを行う
        tag = element.tag.some((tagItem) =>
          tagItem.trim().toLowerCase().includes(this.tagKeyword.toLowerCase()),
        );
        // 並べ替え処理
        if (tag) {
          const sortedTags = [...element.tag].sort((a, b) =>
            this.compareTags(a, b),
          );
          element.tag = sortedTags;
        }
      }
      // フィルター条件を保持
      this.saveFiltersToStorage();

      return (
        deviceName &&
        serialNo &&
        deviceTypeDisplayName &&
        lastCommunicationAt &&
        status &&
        battery &&
        settingGroupName &&
        tag
      );
    };

    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceSettingList'] !== undefined) {
      const deviceSettingList: DeviceTelemetryViewModel[] =
        changes['deviceSettingList'].currentValue;
      this.dataSource.data = deviceSettingList;
    } else if (changes['role'] !== undefined) {
      const role: Role = changes['role'].currentValue;
      this._setDisplayedColumns(role);
    }
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.filteredData);
  }

  /**
   * すべて選択されているか
   *
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * デバイス名でのフィルタリングをクリアする。
   */
  public clearDeviceNameFilter(): void {
    // フィルタ条件をクリア
    this.deviceNameKeyword = '';

    this.applyFilter();
  }

  /**
   * シリアル番号でのフィルタリングをクリアする。
   */
  public clearSerialNoFilter(): void {
    // フィルタ条件をクリア
    this.serialNoKeyword = '';

    this.applyFilter();
  }

  /**
   * 最終更新日時でのフィルタリングをクリアする。
   */
  public clearLastCommunicationAtFilter(): void {
    // フィルタ条件をクリア
    this.lastCommunicationAtKeyword = '';

    this.applyFilter();
  }

  /**
   * ステータスでのフィルタリングをクリアする。
   */
  public clearStatusFilter(): void {
    // フィルタ条件をクリア
    this.statusKeyword = '';

    this.applyFilter();
  }

  /**
   * バッテリーでのフィルタリングをクリアする。
   */
  public clearBatteryFilter(): void {
    // フィルタ条件をクリア
    this.batteryKeyword = '';

    this.applyFilter();
  }

  /**
   * 設定グループでのフィルタリングをクリアする。
   */
  public clearSettingGroupNameFilter(): void {
    // フィルタ条件をクリア
    this.settingGroupNameKeyword = '';

    this.applyFilter();
  }

  /**
   * タグでのフィルタリングをクリアする。
   */
  public clearTagFilter(): void {
    // フィルタ条件をクリア
    this.tagKeyword = '';

    this.applyFilter();
  }

  /**
   * 一括削除する
   */
  public deleteAll() {
    this.deleted.emit(this.selection.selected);
  }

  /**
   * 権限ごとにテーブルカラムを設定する
   * @param role 権限
   */
  private _setDisplayedColumns(role: Role) {
    if (role === 'system_admin') {
      this.displayedColumns = SYSTEM_ADMIN_TABLE_COLUMN_LIST;
    } else if (role === 'tenant_admin') {
      this.displayedColumns = ADMIN_TABLE_COLUMN_LIST;
    } else {
      this.displayedColumns = GENERAL_TABLE_COLUMN_LIST;
    }
  }

  /**
   * 表示するリストの項目を設定する
   */
  public updateDisplayList() {
    this.displayedColumns = this.options
      .filter((option) => option.checked)
      .map((option) => option.name);
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    if (this.pageIndex) {
      setTimeout(() => {
        this.setPageIndex(pageIndex);
      }, 50);
      this.setPageIndex(pageIndex);
    }
  }

  private setPageIndex(pageIndex: number) {
    pageIndex = this.filterService.getValidPageIndex(this.paginator, pageIndex);

    this.paginator.pageIndex = pageIndex;
    this.cdr.detectChanges();
  }

  /**
   * タグを比較してソートする。
   * @param a - 比較する最初のタグ
   * @param b - 比較する2番目のタグ
   * @returns
   */
  private compareTags(a: string, b: string): number {
    const aLower = a.trim().toLowerCase();
    const bLower = b.trim().toLowerCase();
    return aLower.localeCompare(bLower);
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.deviceNameKeyword = filters.deviceName || '';
    this.serialNoKeyword = filters.serialNo || '';
    this.selectedDeviceType = filters.deviceTypeDisplayName || '';
    this.lastCommunicationAtKeyword = filters.lastCommunicationAt || '';
    this.statusKeyword = filters.status || '';
    this.batteryKeyword = filters.battery || '';
    this.settingGroupNameKeyword = filters.settingGroupName || '';
    this.tagKeyword = filters.tag || '';
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: DeviceSettingListFilter = {
      deviceName: this.deviceNameKeyword,
      serialNo: this.serialNoKeyword,
      deviceTypeDisplayName: this.selectedDeviceType,
      lastCommunicationAt: this.lastCommunicationAtKeyword,
      status: this.statusKeyword,
      battery: this.batteryKeyword,
      settingGroupName: this.settingGroupNameKeyword,
      tag: this.tagKeyword,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }
}
